import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { supabase } from '../../config/supabase'; // Importação do Supabase
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Trash2, PlusCircle, Edit2, User, X, Star } from 'lucide-react';
import { Testimonial, TestimonialForm } from '../../types/supabase'; // Novos tipos

// Nota: O tipo Timestamp do Firebase não é mais necessário aqui.

const TestimonialsEditor = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [testimonialsList, setTestimonialsList] = useState<Testimonial[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const { user, selectedClient } = useAuth();
  const currentClientId = selectedClient?.clientId;
  const navigate = useNavigate();
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<TestimonialForm>();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !user.userId) { // Assumindo que user.id é o userId do Supabase
        navigate("/login");
        return;
      }

      if (!currentClientId) {
        toast.error('Selecione uma clínica para gerenciar depoimentos.');
        navigate('/selecionar-cliente');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_clients')
          .select('userRole')
          .eq('userId', user.userId)
          .eq('clientId', currentClientId)
          .single();

        if (error || !data) {
          throw new Error(error?.message || 'Erro ao buscar perfil do usuário');
        }

        if (data.userRole !== 'admin') {
          toast.error('Acesso não autorizado');
          navigate('/');
          return;
        }

        setIsAdmin(true);
        fetchTestimonials(currentClientId);
      } catch (error: any) {
        console.error('Erro ao verificar permissões:', error.message);
        toast.error('Erro ao verificar permissões');
        navigate('/');
      }
    };

    checkAdminStatus();
  }, [user, currentClientId, navigate]);

  const fetchTestimonials = async (clientId?: string) => {
    const scopedClientId = clientId ?? currentClientId;
    if (!scopedClientId) {
      setTestimonialsList([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('clientId', scopedClientId)
        .order('testimonialsPublishDate', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      const testimonials = data.map(item => ({
        testimonialsId: item.testimonialsId,
        userId: item.userId,
        clientId: item.clientId,
        testimonialsName: item.testimonialsName,
        testimonialsProfession: item.testimonialsProfession,
        testimonialsContent: item.testimonialsContent,
        testimonialsFormattedContent: item.testimonialsFormattedContent,
        testimonialsRating: item.testimonialsRating,
        testimonialsStatus: item.testimonialsStatus,
        testimonialsPublishDate: item.testimonialsPublishDate, // Ajustar conforme o tipo de retorno do Supabase
        testimonialsAuthor: item.testimonialsAuthor,
        testimonialsCreatedAt: item.testimonialsCreatedAt,
        testimonialsUpdatedAt: item.testimonialsUpdatedAt,
      })) as Testimonial[];

      setTestimonialsList(testimonials);
    } catch (error: any) {
      console.error('Erro ao carregar depoimentos:', error.message);
      toast.error('Erro ao carregar depoimentos');
    }
  };

  const handleEdit = (testimonial: Testimonial) => {
    setEditingTestimonial(testimonial);
    setShowEditor(true);
    setValue('name', testimonial.testimonialsName);
    setValue('profession', testimonial.testimonialsProfession || '');
    setValue('content', testimonial.testimonialsContent);
    setEditorContent(testimonial.testimonialsFormattedContent || '');
    // store rating as string to match radio input values
    setValue('rating', testimonial.testimonialsRating ?? 0);
  };

  const onSubmit = async (data: TestimonialForm) => {
    if (!user || !isAdmin || !currentClientId) {
      toast.error('Você não tem permissão para gerenciar depoimentos');
      return;
    }

    setIsLoading(true);
    try {
      const now = new Date().toISOString(); // Supabase usa ISO string para timestamps

      const testimonialData = {
        userId: user.userId, // ID do usuário do Supabase
        clientId: currentClientId,
        testimonialsName: data.name.trim(),
        testimonialsProfession: data.profession.trim(),
        testimonialsContent: data.content.trim(),
        testimonialsFormattedContent: editorContent,
  testimonialsRating: Number(data.rating),
        testimonialsAuthor: user.userDisplayName || 'Admin',
  testimonialsPublishDate: now,
        testimonialsCreatedAt: now,
        testimonialsUpdatedAt: now,
        testimonialsStatus: 'published' // Supondo status padrão 'published'
      };

      if (editingTestimonial) {
        const { error } = await supabase
          .from('testimonials')
          .update(testimonialData)
          .eq('testimonialsId', editingTestimonial.testimonialsId)
          .eq('clientId', currentClientId);

        if (error) throw new Error(error.message);
        toast.success('Depoimento atualizado com sucesso!');
      } else {
        const { error } = await supabase
          .from('testimonials')
          .insert([testimonialData]);

        if (error) throw new Error(error.message);
        toast.success('Depoimento publicado com sucesso!');
      }

      reset();
      setShowEditor(false);
      setEditingTestimonial(null);
      fetchTestimonials(currentClientId);
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Erro ao salvar depoimento');
    } finally {
      setIsLoading(false);
    }
  };

  const onSaveDraft = handleSubmit(async (data: TestimonialForm) => {
    if (!user || !isAdmin || !currentClientId) {
      toast.error('Você não tem permissão para gerenciar depoimentos');
      return;
    }

    setIsLoading(true);
    try {
      const now = new Date().toISOString();

      const testimonialData = {
        userId: user.userId,
        clientId: currentClientId,
        testimonialsName: data.name.trim(),
        testimonialsProfession: data.profession.trim(),
        testimonialsContent: data.content.trim(),
        testimonialsFormattedContent: editorContent,
  testimonialsRating: Number(data.rating),
        testimonialsAuthor: user.userDisplayName || 'Admin',
        testimonialsPublishDate: null,
        testimonialsCreatedAt: now,
        testimonialsUpdatedAt: now,
        testimonialsStatus: 'draft'
      };

      if (editingTestimonial) {
        const { error } = await supabase
          .from('testimonials')
          .update(testimonialData)
          .eq('testimonialsId', editingTestimonial.testimonialsId)
          .eq('clientId', currentClientId);

        if (error) throw new Error(error.message);
        toast.success('Rascunho salvo com sucesso!');
      } else {
        const { error } = await supabase
          .from('testimonials')
          .insert([testimonialData]);

        if (error) throw new Error(error.message);
        toast.success('Rascunho criado com sucesso!');
      }

      reset();
      setShowEditor(false);
      setEditingTestimonial(null);
      fetchTestimonials(currentClientId);
    } catch (error) {
      console.error('Error saving draft testimonial:', error);
      toast.error('Erro ao salvar rascunho');
    } finally {
      setIsLoading(false);
    }
  });

  const handleDelete = async (testimonialsId: string) => {
    if (!currentClientId) {
      toast.error('Selecione uma clínica para excluir depoimentos.');
      return;
    }

    try {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('testimonialsId', testimonialsId)
        .eq('clientId', currentClientId);

      if (error) throw new Error(error.message);

      toast.success('Depoimento excluído com sucesso!');
      fetchTestimonials(currentClientId);
    } catch (error: any) {
      console.error('Erro ao excluir depoimento:', error.message);
      toast.error('Erro ao excluir depoimento');
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const renderStars = (ratingValue: number) => {
    return Array(5).fill(0).map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < ratingValue ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
      />
    ));
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Gerenciar Depoimentos</h2>
        {!showEditor && (
          <button
            onClick={() => {
              setEditingTestimonial(null);
              reset();
              setShowEditor(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Novo Depoimento
          </button>
        )}
      </div>

      {showEditor ? (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-semibold mb-6">
            {editingTestimonial ? 'Editar Depoimento' : 'Novo Depoimento'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nome do Paciente</label>
                <input
                  type="text"
                  {...register('name', { required: 'Nome é obrigatório' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Profissão/Cidade</label>
                <input
                  type="text"
                  {...register('profession', { required: 'Profissão/Cidade é obrigatória' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Ex: Engenheiro, Londrina-PR"
                />
                {errors.profession && (
                  <p className="mt-1 text-sm text-red-600">{errors.profession.message}</p>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Depoimento (Texto Simples)</label>
              <textarea
                {...register('content', { required: 'Depoimento é obrigatório' })}
                rows={5}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Depoimento (Formatado)</label>
              <div className="border rounded-md">
                <ReactQuill 
                  theme="snow" 
                  value={editorContent} 
                  onChange={setEditorContent}
                  modules={modules}
                  className="h-48 mb-12"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Use as ferramentas acima para formatar o texto com negrito, itálico, cores, tamanhos, etc.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Avaliação (1-5 estrelas)</label>
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label key={value} className="cursor-pointer relative">
                    <input
                      type="radio"
                      value={value}
                      {...register('rating', { required: 'Avaliação é obrigatória' })}
                      className="sr-only"
                    />
                    <Star 
                      className={`w-8 h-8 hover:text-yellow-500 ${
                        Number(editingTestimonial?.testimonialsRating) === value || 
                        Number(watch('rating')) === value ? 
                        'text-yellow-500 fill-yellow-500' : 'text-gray-300'
                      }`}
                      onClick={() => setValue('rating', value, { shouldValidate: true })}
                    />
                  </label>
                ))}
              </div>
              {errors.rating && (
                <p className="mt-1 text-sm text-red-600">{errors.rating.message}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowEditor(false);
                  setEditingTestimonial(null);
                  reset();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Cancelar
              </button>
              {(!editingTestimonial || editingTestimonial.testimonialsStatus === 'draft') && (
                <button
                  type="button"
                  onClick={onSaveDraft}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  {isLoading ? 'Salvando...' : 'Salvar como rascunho'}
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? 'Salvando...' : (
                  editingTestimonial
                    ? (editingTestimonial.testimonialsStatus === 'draft' ? 'Publicar Depoimento' : 'Salvar Alterações')
                    : 'Publicar Depoimento'
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Paciente
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Avaliação
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Data
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {testimonialsList.map((testimonial) => (
                  <tr key={testimonial.testimonialsId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{testimonial.testimonialsName}</div>
                          <div className="text-sm text-gray-500">{testimonial.testimonialsProfession}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center mb-1">
                        {renderStars(testimonial.testimonialsRating)}
                      </div>
                      <div className="text-sm text-gray-500 line-clamp-2">{testimonial.testimonialsContent}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 whitespace-nowrap">
                        {new Date(testimonial.testimonialsPublishDate).toLocaleDateString('pt-BR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(testimonial)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Editar"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(testimonial.testimonialsId)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {testimonialsList.length === 0 && (
              <div className="text-center py-8">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum depoimento publicado</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir este depoimento? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm!)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsEditor;
